
import { logger, setRequestId } from '../../../utils/logger';

const API_URL = import.meta.env.VITE_API_URL;
const TOKEN_KEY = 'peart_auth_token';

// Read X-Request-ID from response and propagate it for log correlation
function captureRequestId(response) {
  const id = response.headers.get('X-Request-ID');
  if (id) setRequestId(id);
}

const getAuthHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    logger.warn('No auth token found', 'apiClient');
    return {};
  }
  logger.debug('Using auth token', 'apiClient', { token_prefix: token.substring(0, 10) });
  return { 'Authorization': `Bearer ${token}` };
};

const checkAndRefreshToken = async () => {
  const expiry = localStorage.getItem('peart_auth_token_expiry');
  if (!expiry) return;

  const now = Date.now();
  const timeUntilExpiry = parseInt(expiry) - now;

  // Wenn Token in weniger als 5 Minuten abläuft, refreshen
  if (timeUntilExpiry < 5 * 60 * 1000) {
    logger.info('Token expires soon, attempting refresh', 'apiClient', { time_until_expiry_ms: timeUntilExpiry });
    if (window.oidcAuth?.isAuthenticated) {
      try {
        await window.oidcAuth.signinSilent();
        logger.info('Token refreshed successfully', 'apiClient');
      } catch (error) {
        logger.error('Failed to refresh token', 'apiClient', { error: error.message });
      }
    }
  }
};

async function handleAuthError(response, context = '', retryFunction = null) {
  if (response.status === 401 || response.status === 403) {
    logger.error('Auth error', 'apiClient', { context, status: response.status, status_text: response.statusText });

    // Try to refresh token once before giving up
    if (retryFunction && window.oidcAuth?.isAuthenticated) {
      try {
        logger.info('Attempting token refresh for retry', 'apiClient', { context });
        await window.oidcAuth.signinSilent();

        // Retry the original request
        const retryResponse = await retryFunction();
        if (retryResponse.ok) {
          logger.info('Retry successful after token refresh', 'apiClient', { context });
          return retryResponse;
        }
      } catch (error) {
        logger.error('Token refresh failed', 'apiClient', { context, error: error.message });
      }
    }
    
    // Show a brief message before logout
    const message = 'Ihre Sitzung ist abgelaufen. Sie werden zum Login weitergeleitet...';
    
    // Create a temporary notification
    const notification = document.createElement('div');
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #dc2626;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 9999;
      font-family: system-ui, -apple-system, sans-serif;
    `;
    document.body.appendChild(notification);
    
    // Remove notification and redirect after 2 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
      performLogout();
    }, 2000);
    
    throw new Error('Sitzung abgelaufen.');
  }
}

function performLogout() {
  localStorage.removeItem('peart_auth_token');
  localStorage.removeItem('peart_auth_token_expiry');
  
  // Redirect to login
  window.location.href = '/login';
}

const apiClient = {
  fetchData: async (type) => {
    // Check and refresh token before any API call to prevent auth errors
    await checkAndRefreshToken();
    
    const response = await fetch(`${API_URL}/${type}`, {
      headers: getAuthHeaders()
    });
    handleAuthError(response);
    if (!response.ok) throw new Error(`Failed to fetch ${type}`);
    return response.json();
  },

  createData: async (type, data) => {
    if (type === 'paintings') {
      return apiClient.createPainting(data);
    }
    if (type === 'news') {
      return apiClient.createNews(data);
    }
    if (type === 'awards') {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        formData.append(key, data[key]);
      });
      const response = await fetch(`${API_URL}/${type}`, {
        method: 'POST',
        headers: { 
          ...getAuthHeaders()
        },
        body: formData,
      });
      handleAuthError(response);
      if (!response.ok) throw new Error(`Failed to create ${type}`);
      return response.json();
    }
    if (type === 'exhibitions') {
      const response = await apiClient.createExhibition(data);
      // createExhibition gibt schon das Response-Objekt zurück, daher dort prüfen
      return response;
    }
    if (type === 'colors') {
      return apiClient.createColor(data);
    }
    const response = await fetch(`${API_URL}/${type}`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(data),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error(`Failed to create ${type}`);
    return response.json();
  },

  updateData: async (type, id, data) => {
    if (type === 'paintings') {
      return apiClient.updatePainting(id, data);
    }
    if (type === 'news') {
      return apiClient.updateNews(id, data);
    }
    if (type === 'home') {
      return apiClient.updateHome(data);
    }
    const response = await fetch(`${API_URL}/${type}/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(data),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error(`Failed to update ${type}`);
    return response.json();
  },

  deleteData: async (type, id) => {
    const response = await fetch(`${API_URL}/${type}/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    handleAuthError(response);
    if (!response.ok) throw new Error(`Failed to delete ${type}`);
    return response.json();
  },

  // Series API calls
  createSeries: async (seriesData) => {
    const response = await fetch(`${API_URL}/series`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(seriesData),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to create series');
    return response.json();
  },

  updateSeries: async (id, seriesData) => {
    const response = await fetch(`${API_URL}/series/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(seriesData),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to update series');
    return response.json();
  },

  deleteSeries: async (id) => {
    const response = await fetch(`${API_URL}/series/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to delete series');
    return response.json();
  },

  // Color API calls
  createColor: async (colorData) => {
    const response = await fetch(`${API_URL}/colors`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(colorData),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error(`Failed to create color`);
    return response.json();
  },

  updateColor: async (id, colorData) => {
    const response = await fetch(`${API_URL}/colors/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(colorData),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to update color');
    return response.json();
  },

  deleteColor: async (id) => {
    const response = await fetch(`${API_URL}/colors/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to delete color');
    return response.json();
  },

  // Award API calls
  createAward: async (awardData) => {
    const formData = new FormData();
    Object.keys(awardData).forEach(key => {
      formData.append(key, awardData[key]);
    });
    const response = await fetch(`${API_URL}/awards`, {
      method: 'POST',
      headers: { 
        ...getAuthHeaders()
      },
      body: formData,
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to create award');
    return response.json();
  },

  updateAward: async (id, awardData) => {
    const response = await fetch(`${API_URL}/awards/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(awardData),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to update award');
    return response.json();
  },

  deleteAward: async (id) => {
    const response = await fetch(`${API_URL}/awards/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to delete award');
    return response.json();
  },

  // Exhibition API calls
  createExhibition: async (exhibitionData) => {
    const formData = new FormData();
    Object.keys(exhibitionData).forEach(key => {
      formData.append(key, exhibitionData[key]);
    });
    const response = await fetch(`${API_URL}/exhibitions`, {
      method: 'POST',
      headers: { 
        ...getAuthHeaders()
      },
      body: formData,
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to create exhibition');
    return response.json();
  },

  updateExhibition: async (id, exhibitionData) => {
    const response = await fetch(`${API_URL}/exhibitions/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(exhibitionData),
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to update exhibition');
    return response.json();
  },

  deleteExhibition: async (id) => {
    const response = await fetch(`${API_URL}/exhibitions/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to delete exhibition');
    return response.json();
  },

  // Painting API calls
  createPainting: async (paintingData) => {
    // Check and refresh token before long upload operation
    await checkAndRefreshToken();
    
    const formData = new FormData();
    
    // Add all text fields
    Object.keys(paintingData).forEach(key => {
      if (key === 'images') {
        // Handle images
        paintingData[key].forEach(image => {
          if (image.file instanceof File) {
            // Neu hochgeladenes Bild
            formData.append('images', image.file);
          } else if (image instanceof File) {
            // Direktes File-Objekt (sollte nicht vorkommen, aber zur Sicherheit)
            formData.append('images', image);
          }
          // Bestehende Bilder (mit string URLs) werden nicht nochmal hochgeladen
        });
      } else if (Array.isArray(paintingData[key])) {
        // Handle arrays (color_ids, award_ids, etc.)
        paintingData[key].forEach(value => {
          formData.append(`${key}[]`, value);
        });
      } else if (key === 'main_image_idx') {
        // Handle main image index specifically
        formData.append('main_image_index', paintingData[key]);
      } else if (key === 'featured') {
        // Ensure boolean is sent as 0/1
        formData.append('featured', paintingData[key] ? 1 : 0);
      } else {
        // Handle regular fields
        formData.append(key, paintingData[key]);
      }
    });

    const response = await fetch(`${API_URL}/paintings`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    
    captureRequestId(response);
    handleAuthError(response, 'create painting');
    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Create painting failed', 'apiClient', { status: response.status, error: errorText });
      throw new Error(`Failed to create painting: ${response.status} ${response.statusText} - ${errorText}`);
    }
    
    return response.json();
  },

  updatePainting: async (id, paintingData) => {
    // Check and refresh token before long upload operation
    await checkAndRefreshToken();
    
    const formData = new FormData();
    
    // Add all text fields
    Object.keys(paintingData).forEach(key => {
      if (key === 'images') {
        // Handle images
        paintingData[key].forEach((image, index) => {
          if (image.file instanceof File) {
            // Neu hochgeladenes Bild
            formData.append('images', image.file);
          } else if (image instanceof File) {
            // Direktes File-Objekt (sollte nicht vorkommen, aber zur Sicherheit)
            formData.append('images', image);
          }
          // existing images are skipped (no upload needed)
        });
      } else if (key === 'delete_image_ids' && Array.isArray(paintingData[key])) {
        // Handle delete_image_ids specifically
        paintingData[key].forEach(id => {
          formData.append('delete_image_ids', id);
        });
      } else if (Array.isArray(paintingData[key])) {
        // Handle arrays (color_ids, award_ids, etc.)
        if (paintingData[key].length > 0) {
          paintingData[key].forEach(value => {
            formData.append(`${key}[]`, value);
          });
        }
      } else if (key === 'main_image_idx') {
        // Handle main image index specifically
        formData.append('main_image_index', paintingData[key]);
      } else if (key === 'featured') {
        // Ensure boolean is sent as 0/1
        formData.append('featured', paintingData[key] ? 1 : 0);
      } else {
        // Handle regular fields
        formData.append(key, paintingData[key]);
      }
    });

    const response = await fetch(`${API_URL}/paintings/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: formData,
    });

    captureRequestId(response);
    handleAuthError(response, 'update painting');
    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Update painting failed', 'apiClient', { painting_id: id, status: response.status, error: errorText });
      throw new Error(`Failed to update painting: ${response.status} ${response.statusText} - ${errorText}`);
    }
    
    return response.json();
  },

  deletePainting: async (id) => {
    const response = await fetch(`${API_URL}/paintings/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to delete painting');
    return response.json();
  },

  // Home API calls
  updateHome: async (homeData) => {
    const formData = new FormData();
    Object.keys(homeData).forEach(key => {
      if (homeData[key] !== null && homeData[key] !== undefined) {
        formData.append(key, homeData[key]);
      }
    });
    const response = await fetch(`${API_URL}/home`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: formData,
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to update home');
    return response.json();
  },

  // News API calls
  createNews: async (newsData) => {
    // Check and refresh token before long upload operation
    await checkAndRefreshToken();
    
    const formData = new FormData();
    formData.append('text_de', newsData.text_de);
    formData.append('text_en', newsData.text_en);
    
    // Handle images
    if (Array.isArray(newsData.images)) {
      const existingImages = newsData.images.filter(img => typeof img === 'string');
      const newImages = newsData.images.filter(img => img instanceof File);
      
      if (existingImages.length > 0) {
        formData.append('existing_images', JSON.stringify(existingImages));
      }
      
      newImages.forEach(img => {
        formData.append('images', img);
      });
    }
    
    // Handle videos
    if (Array.isArray(newsData.videos)) {
      const existingVideos = newsData.videos.filter(vid => typeof vid === 'string');
      const newVideos = newsData.videos.filter(vid => vid instanceof File);
      
      if (existingVideos.length > 0) {
        formData.append('existing_videos', JSON.stringify(existingVideos));
      }
      
      newVideos.forEach(vid => {
        formData.append('videos', vid);
      });
    }

    const response = await fetch(`${API_URL}/news`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    captureRequestId(response);
    handleAuthError(response);
    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Create news failed', 'apiClient', { status: response.status, error: errorText });
      throw new Error(`Failed to create news: ${response.status} ${errorText}`);
    }
    const result = await response.json();
    logger.info('News created successfully', 'apiClient', { news_id: result.id });
    return result;
  },

  updateNews: async (id, newsData) => {
    // Check and refresh token before long upload operation
    await checkAndRefreshToken();
    
    const formData = new FormData();
    formData.append('text_de', newsData.text_de);
    formData.append('text_en', newsData.text_en);
    
    // Handle images
    if (Array.isArray(newsData.images)) {
      const existingImages = newsData.images.filter(img => typeof img === 'string');
      const newImages = newsData.images.filter(img => img instanceof File);
      
      if (existingImages.length > 0) {
        formData.append('existing_images', JSON.stringify(existingImages));
      }
      
      newImages.forEach(img => {
        formData.append('images', img);
      });
    }
    
    // Handle videos
    if (Array.isArray(newsData.videos)) {
      const existingVideos = newsData.videos.filter(vid => typeof vid === 'string');
      const newVideos = newsData.videos.filter(vid => vid instanceof File);
      
      if (existingVideos.length > 0) {
        formData.append('existing_videos', JSON.stringify(existingVideos));
      }
      
      newVideos.forEach(vid => {
        formData.append('videos', vid);
      });
    }

    const response = await fetch(`${API_URL}/news/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: formData,
    });
    captureRequestId(response);
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to update news');
    return response.json();
  },

  deleteNews: async (id) => {
    const response = await fetch(`${API_URL}/news/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    handleAuthError(response);
    if (!response.ok) throw new Error('Failed to delete news');
    return response.json();
  }
};

export const { fetchData, createData, updateData, deleteData } = apiClient; 